import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, Tab, Tabs } from '@readcloud/component-library';
import React from 'react';
import { useForm } from 'react-final-form';
import * as yup from 'yup';
import { Forms } from '../..';
import { ColorPicker } from '../Drawings';
import { rgbToString } from '../Drawings/utils';
import {
  AnnotationEditorFormProps,
  makeAnnotationEditor,
} from '../makeAnnotationEditor';
const fonts = ['Times New Roman', 'Arial'];
const fontSizes = [8, 10, 12, 16, 20, 24, 28, 32, 48, 72, 86];

const schema = yup
  .object({
    text: yup.string().required(),
    fill: yup.string().required(),
    fontFamily: yup.string().required(),
    fontSize: yup.number().required(),
    fontWeight: yup.number().required(),
    textAlign: yup
      .mixed<'left' | 'right' | 'center'>()
      .oneOf(['left', 'right', 'center'])
      .required(),
    overflowWrap: yup
      .mixed<'break-word' | 'normal'>()
      .oneOf(['break-word', 'normal'])
      .required(),
    wordBreak: yup
      .mixed<'break-all' | 'keep-all' | 'normal'>()
      .oneOf(['break-all', 'keep-all', 'normal'])
      .required(),
    lineHeight: yup.number().required(),
    rect: yup
      .object({
        x: yup.number(),
        y: yup.number(),
        width: yup.number(),
        height: yup.number(),
      })
      .required(),
    publisherOptions: Forms.commonSchemas.publisherOptions,
  })
  .shape(Forms.commonSchemas.commonFields);
function OverlayTextAnnotationEditorForm<FormValues extends object>(
  props: AnnotationEditorFormProps
) {
  const form = useForm();
  const state = form.getState();

  return (
    <div className="rc-flex rc-flex-col rc-px-2">
      <div className="rc-flex rc-flex-col">
        <span>Text Color</span>
        <ColorPicker
          className="rc-overflow-hidden"
          color={state.values.fill}
          onChange={(change) => {
            form.change('fill', rgbToString(change.rgb));
          }}
        />
      </div>
      <div className="rc-p-1" />
      <div className="rc-flex rc-flex-col">
        <span>Text Align</span>
        <div className="rc-p-1" />
        <Tabs
          className="rc-w-full"
          onSelect={(v) => {
            form.change('textAlign', v);
          }}
          activeKey={state.values.textAlign}
          fill
        >
          {[
            <Tab
              tabClassName="rc-w-full"
              eventKey="left"
              title={<FontAwesomeIcon icon={faAlignLeft} />}
            />,
            <Tab
              tabClassName="rc-w-full"
              eventKey="center"
              title={<FontAwesomeIcon icon={faAlignCenter} />}
            />,
            <Tab
              tabClassName="rc-w-full"
              title={<FontAwesomeIcon icon={faAlignRight} />}
              eventKey="right"
            />,
          ]}
        </Tabs>

        <div className="rc-p-1" />

        <span>Font Family</span>
        <div className="rc-p-1" />
        <Select
          matchWidth
          options={fonts.map((font) => ({ label: font, value: font }))}
          onSelect={(option) => {
            form.change('fontFamily', option.value);
          }}
          value={state.values.fontFamily}
        />

        <div className="rc-p-1" />

        <span>Font Size</span>
        <div className="rc-p-1" />
        <Select
          matchWidth
          options={fontSizes.map((size) => ({
            label: size.toString(),
            value: size,
          }))}
          onSelect={(option) => {
            form.change('fontSize', option.value);
          }}
          value={state.values.fontSize}
        />
        <div className="rc-py-1"></div>
        <div className="rc-flex rc-flex-col">
          <Forms.commonElements.CommonFields
            showPublisherOptions={props.showPublisherOptions}
            showDescription
          />
        </div>
      </div>
    </div>
  );
}

export const OverlayTextAnnotationEditor = React.memo(
  makeAnnotationEditor({
    FormComponent: OverlayTextAnnotationEditorForm,
    // @ts-expect-error: This error goes away once "strictNullChecks" are set to true
    schema,
  })
);
